const W = 1080
const H = 1350
const LIME = '#a8ff3e'
const INK = '#1a2600'
const BG = '#0b0d0a'
const FLAME =
  'M40 16C44 30 54 36 56 50C60 42 59 34 57 28C66 36 70 46 70 58C70 72 60 82 48 82C36 82 26 72 26 59C26 44 38 36 40 16ZM48 54C52 60 56.5 63 56.5 69C56.5 74 52.5 78 48 78C43.5 78 39.5 74 39.5 69C39.5 63 44 60 48 54Z'

export type WeekCard = {
  weekLabel: string
  tasks: string
  checkIns: string
  tasksN: number
  checkInsN: number
  footer: string
  days: number[]
  elapsed: number
  dayLetters: string[]
  shareText: string
}

const FONT = "'Plus Jakarta Sans', system-ui, sans-serif"

export function drawWeekCard(c: WeekCard) {
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')!

  ctx.fillStyle = BG
  ctx.fillRect(0, 0, W, H)
  const glow = ctx.createRadialGradient(W * 0.8, H * 0.35, 0, W * 0.8, H * 0.35, W * 0.8)
  glow.addColorStop(0, 'rgba(168,255,62,0.18)')
  glow.addColorStop(1, 'rgba(168,255,62,0)')
  ctx.fillStyle = glow
  ctx.fillRect(0, 0, W, H)

  // Brand: lime tile with the flame, then the wordmark.
  ctx.fillStyle = LIME
  ctx.beginPath()
  ctx.roundRect(88, 88, 96, 96, 24)
  ctx.fill()
  ctx.save()
  ctx.translate(136, 136)
  ctx.scale(0.86, 0.86)
  ctx.translate(-48, -49)
  ctx.fillStyle = INK
  ctx.fill(new Path2D(FLAME), 'evenodd')
  ctx.restore()
  ctx.fillStyle = '#fff'
  ctx.font = `800 52px ${FONT}`
  ctx.textBaseline = 'middle'
  ctx.fillText('Goaltree', 208, 138)

  ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = 'rgba(255,255,255,0.6)'
  ctx.font = `700 44px ${FONT}`
  ctx.fillText(c.weekLabel, 88, 330)

  const stat = (n: number, label: string, y: number, color: string) => {
    ctx.fillStyle = color
    ctx.font = `800 200px ${FONT}`
    ctx.fillText(String(n), 80, y)
    const w = ctx.measureText(String(n)).width
    ctx.fillStyle = '#fff'
    ctx.font = `700 52px ${FONT}`
    // The plural label repeats the number ("12 tasks"); keep just the word.
    ctx.fillText(label.replace(/^\s*[\d,.]+\s*/, ''), 80 + w + 28, y)
  }
  stat(c.tasksN, c.tasks, 560, LIME)
  stat(c.checkInsN, c.checkIns, 780, '#fff')

  // Week bars, Monday first; days still to come are dashed outlines.
  const max = Math.max(1, ...c.days)
  const bw = 96
  const gap = (W - 176 - bw * 7) / 6
  const base = 1090
  c.days.forEach((n, i) => {
    const x = 88 + i * (bw + gap)
    const h = i >= c.elapsed ? 40 : Math.max(16, (n / max) * 190)
    ctx.beginPath()
    ctx.roundRect(x, base - h, bw, h, [14, 14, 6, 6])
    if (i >= c.elapsed) {
      ctx.setLineDash([8, 8])
      ctx.strokeStyle = 'rgba(255,255,255,0.25)'
      ctx.lineWidth = 3
      ctx.stroke()
      ctx.setLineDash([])
    } else {
      ctx.fillStyle = n > 0 ? LIME : 'rgba(255,255,255,0.1)'
      ctx.fill()
    }
    ctx.fillStyle = 'rgba(255,255,255,0.5)'
    ctx.font = `700 34px ${FONT}`
    ctx.textAlign = 'center'
    ctx.fillText(c.dayLetters[i], x + bw / 2, base + 52)
    ctx.textAlign = 'left'
  })

  ctx.fillStyle = 'rgba(255,255,255,0.75)'
  ctx.font = `700 40px ${FONT}`
  ctx.fillText(c.footer, 88, 1250)
  ctx.fillStyle = LIME
  ctx.textAlign = 'right'
  ctx.fillText('goaltree.ai', W - 88, 1250)
  ctx.textAlign = 'left'
  return canvas
}

// Shares the recap as a PNG where the Web Share API takes files (phones);
// otherwise downloads it.
export async function shareWeekCard(c: WeekCard) {
  await document.fonts.load(`800 52px ${FONT}`).catch(() => {})
  const blob = await new Promise<Blob | null>((res) => drawWeekCard(c).toBlob(res, 'image/png'))
  if (!blob) return
  const file = new File([blob], 'goaltree-week.png', { type: 'image/png' })
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text: c.shareText })
    } catch {}
    return
  }
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = file.name
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
